import { Injectable, signal } from '@angular/core';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signOut } from 'aws-amplify/auth';

export interface UserInfo {
	firstName: string | undefined,
	lastName: string | undefined,
	email: string | undefined
}

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private readonly _loggedIn = signal<boolean>(false);
	public readonly loggedIn = this._loggedIn.asReadonly();

	private readonly _isInstaller = signal<boolean>(false);
	public readonly isInstaller = this._isInstaller.asReadonly();

	private readonly _isInstallerDSO = signal<boolean>(false);
	public readonly isInstallerDSO = this._isInstallerDSO.asReadonly();

	private readonly _isAdmin = signal<boolean>(false);
	public readonly isAdmin = this._isAdmin.asReadonly();

	private readonly _isSupport = signal<boolean>(false);
	public readonly isSupport = this._isSupport.asReadonly();

	private readonly _userInfo = signal<UserInfo | undefined>(undefined);
	public readonly userInfo = this._userInfo.asReadonly();

	private readonly _userGroups = signal<string[]>([]);
	public readonly userGroups = this._userGroups.asReadonly();

	constructor() { }

	public async refreshAuthState(): Promise<void> {
		try {
			await getCurrentUser();
			this._loggedIn.set(true);

			const [attrs, groups] = await Promise.all([
				fetchUserAttributes(),
				this.getUserGroups(),
			]);

			this._userInfo.set({
				firstName: attrs.name,
				lastName: attrs.family_name,
				email: attrs.email,
			});
			this._isInstaller.set(groups?.includes('Installer') ?? false);
			this._isInstallerDSO.set(groups?.includes('DSO_Installer') ?? false);
			this._isAdmin.set(groups?.includes('Admin') ?? false);
			this._isSupport.set(groups?.includes('Ratio_Support') ?? false);
			this._userGroups.set(groups ?? []);
		} catch {
			this._loggedIn.set(false);
			this._isInstaller.set(false);
			this._isInstallerDSO.set(false);
			this._isAdmin.set(false);
			this._isSupport.set(false);
			this._userInfo.set(undefined);
			this._userGroups.set([]);
		}
	}

	async isLoggedIn(): Promise<boolean> {
		try {
			await getCurrentUser();
			return true;
		}
		catch {
			return false;
		}
	}

	async getUserId(): Promise<string | undefined> {
		try {
			return (await getCurrentUser()).userId;
		}
		catch {
			return undefined;
		}
	}

	async getToken(): Promise<string | undefined> {
		try {
			return (await fetchAuthSession()).tokens?.accessToken?.toString();
		}
		catch {
			return undefined;
		}
	}

	async getUserInfo(): Promise<UserInfo | undefined> {
		try {
			const userAttributes = await fetchUserAttributes()
			return {
				firstName: userAttributes.name,
				lastName: userAttributes.family_name,
				email: userAttributes.email
			};
		} catch {
			return undefined;
		}
	}

	async getUserGroups(): Promise<string[] | undefined> {
		try {
			const session = await fetchAuthSession();
			const idToken = session.tokens?.idToken;
			if (idToken?.payload['cognito:groups']) {
				return idToken.payload['cognito:groups'] as string[];
			}
			return undefined;
		} catch {
			return undefined;
		}
	}

	async logOut() {
		await signOut();
		this._loggedIn.set(false);
		this._isInstaller.set(false);
		this._isInstallerDSO.set(false);
		this._isAdmin.set(false);
		this._isSupport.set(false);
		this._userInfo.set(undefined);
		this._userGroups.set([]);
	}
}
